/*
 * Вкладки одного браузера живут как одна (спека 2026-10-02-tabs-devices-sync,
 * раздел 1): вошёл в одной — вошёл во всех, вышел в одной — вышел во всех.
 * Сессия у вкладок общая (одна cookie), а сервер о выходе соседней вкладки
 * не скажет: вкладка, из которой вышли, с ним уже не связана. Поэтому вести
 * идут каналом браузера, без сервера, и другие устройства не трогают.
 */

/** Почему вышли — от этого зависит надпись на окне входа; null — просто «Выйти». */
export type OutReason = 'idle' | 'expired' | 'disabled' | 'password' | null;

export type TabNews = { type: 'login'; userId: string } | { type: 'logout'; reason: OutReason };

const CHANNEL = 'code-tabs';

/**
 * Канал вкладки: один на всю вкладку, и для вести, и для слушания. Объект
 * канала не слышит собственных вестей, поэтому вкладка не обрабатывает свой же
 * выход второй раз. Канала нет (старый браузер) или он не создался (закрытая
 * рамка, запрет на хранилище) — вкладки живут каждая сама по себе; отказ
 * запоминаем, чтобы не пробовать заново на каждый вызов.
 */
let shared: BroadcastChannel | null | undefined;

function channel(): BroadcastChannel | null {
  if (shared === undefined) {
    try {
      shared = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL) : null;
    } catch {
      shared = null;
    }
  }
  return shared;
}

/** Сказать остальным вкладкам этого браузера. Себе весть не приходит. */
export function tellTabs(news: TabNews): void {
  channel()?.postMessage(news);
}

/** Слушать соседние вкладки; возвращает отписку. Общий канал отписка не закрывает. */
export function onTabs(fn: (news: TabNews) => void): () => void {
  const ch = channel();
  if (!ch) return () => {};
  const listener = (e: MessageEvent) => fn(e.data as TabNews);
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

/**
 * Столько держится отметка «уже сделано»: соседние вкладки в это окно то же
 * самое не повторяют.
 */
const ONCE_TTL_MS = 5000;
const ONCE_PREFIX = 'code-once:';

/** Прибирает отметки старше минуты: иначе они копятся без конца. */
function sweepOnce(now: number): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(ONCE_PREFIX)) continue;
    if (now - Number(localStorage.getItem(k) ?? 0) > 60_000) localStorage.removeItem(k);
  }
}

/**
 * Занять дело за этой вкладкой: `true` — делать здесь, `false` — уже
 * сделала соседняя. Так звук сообщения звучит один раз (жалоба Ильи
 * 21.09.2026) и уведомление на рабочем столе показывается одно. `key` — про
 * что дело: номер сообщения, номер уведомления. Памяти нет (частное окно,
 * запрет на хранилище) — делаем: лучше два раза, чем ни одного.
 */
export function claimOnce(key: string, now = Date.now(), ttlMs = ONCE_TTL_MS): boolean {
  try {
    const mark = `${ONCE_PREFIX}${key}`;
    if (now - Number(localStorage.getItem(mark) ?? 0) < ttlMs) return false;
    localStorage.setItem(mark, String(now));
    sweepOnce(now);
    return true;
  } catch {
    return true;
  }
}
